import React from "react";
import { Box, Paper, Skeleton, Grid } from "@mui/material";

// ─── reusable shimmer pieces ──────────────────────────────────────────────────

/** MUI outlined Select shimmer — label floats above the box */
const ShimmerSelect: React.FC<{ labelWidth?: number }> = ({
  labelWidth = 120,
}) => (
  <Box sx={{ position: "relative" }}>
    {/* Floating label chip */}
    <Skeleton
      variant="rectangular"
      width={labelWidth}
      height={12}
      animation="wave"
      sx={{
        position: "absolute",
        top: -6,
        left: 12,
        zIndex: 1,
        borderRadius: 1,
        bgcolor: "grey.200",
      }}
    />
    {/* Select box */}
    <Skeleton
      variant="rectangular"
      width="100%"
      height={56}
      animation="wave"
      sx={{ borderRadius: 1.5, bgcolor: "grey.100" }}
    />
  </Box>
);

/**
 * Autocomplete field shimmer.
 * Optionally shows a "Not found? Click here" link shimmer on the right.
 */
const ShimmerAutocomplete: React.FC<{
  labelWidth?: number;
  showNotFound?: boolean;
}> = ({ labelWidth = 140, showNotFound = false }) => (
  <Box>
    {/* Label row */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 0.75,
      }}
    >
      <Skeleton
        variant="rectangular"
        width={labelWidth}
        height={12}
        animation="wave"
        sx={{ borderRadius: 1, bgcolor: "grey.200" }}
      />
      {showNotFound && (
        <Skeleton
          variant="rectangular"
          width={130}
          height={12}
          animation="wave"
          sx={{ borderRadius: 1, bgcolor: "primary.light", opacity: 0.35 }}
        />
      )}
    </Box>
    {/* Input box */}
    <Skeleton
      variant="rectangular"
      width="100%"
      height={56}
      animation="wave"
      sx={{ borderRadius: 1.5, bgcolor: "grey.100" }}
    />
  </Box>
);

// ─── main skeleton ────────────────────────────────────────────────────────────

const Step2CourseDiscoverySkeleton: React.FC = () => (
  <Box>
    {/* ── Section header ── */}
    <Box sx={{ mb: 4 }}>
      {/* "✦ Course Discovery" breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Skeleton
          variant="rectangular"
          width={13}
          height={13}
          animation="wave"
          sx={{ borderRadius: 0.5 }}
        />
        <Skeleton
          variant="rectangular"
          width={120}
          height={13}
          animation="wave"
          sx={{ borderRadius: 1 }}
        />
      </Box>

      {/* "Tell us about your Dream University" h3 */}
      <Skeleton
        variant="rectangular"
        width="58%"
        height={38}
        animation="wave"
        sx={{ borderRadius: 1.5, mb: 1.5 }}
      />

      {/* Subtitle */}
      <Skeleton
        variant="rectangular"
        width="72%"
        height={14}
        animation="wave"
        sx={{ borderRadius: 1, mb: 0.75 }}
      />
      <Skeleton
        variant="rectangular"
        width="48%"
        height={14}
        animation="wave"
        sx={{ borderRadius: 1 }}
      />
    </Box>

    {/* ── Form card ── */}
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        bgcolor: "#FAFCFB",
      }}
    >
      <Grid container spacing={2.5}>

        {/* Row 1: Course Category | Admission Status */}
        <Grid item xs={12} sm={6}>
          <ShimmerSelect labelWidth={120} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ShimmerSelect labelWidth={130} />
        </Grid>

        {/* Row 2: Study Destination (autocomplete, no link) | University Name (with Not found?) */}
        <Grid item xs={12} sm={6}>
          <ShimmerAutocomplete labelWidth={145} showNotFound={false} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ShimmerAutocomplete labelWidth={130} showNotFound={true} />
        </Grid>

        {/* Row 3: Institute Name (with Not found?) | Course Name (with Not found?) */}
        <Grid item xs={12} sm={6}>
          <ShimmerAutocomplete labelWidth={120} showNotFound={true} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ShimmerAutocomplete labelWidth={115} showNotFound={true} />
        </Grid>

        {/* Row 4: Course Type (half-width, left column only) */}
        <Grid item xs={12} sm={6}>
          <ShimmerSelect labelWidth={95} />
        </Grid>

        {/* Empty right column to maintain grid alignment */}
        <Grid item xs={12} sm={6} />

      </Grid>

      {/* ── Action buttons — bottom right ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 3,
        }}
      >
        {/* Back button */}
        <Skeleton
          variant="rectangular"
          width={100}
          height={50}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: "grey.200" }}
        />
        {/* Continue button */}
        <Skeleton
          variant="rectangular"
          width={140}
          height={50}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: "grey.300" }}
        />
      </Box>
    </Paper>
  </Box>
);

export default Step2CourseDiscoverySkeleton;